import { useRef, useState } from 'react';
import type { SessionItem, SessionRecord } from '../types';
import { fmtClock, segmentTranscript } from '../lib/metrics';
import { exportSessionHTML, downloadBlob } from '../lib/export';
import { toast } from '../lib/toast';
import { APP_VERSION } from '../version';

export default function Review({ record, onAgain }: { record: SessionRecord; onAgain: () => void }) {
  const s = record.summary;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const skippedNoKey = record.items.some((i) => i.transcript_status === 'skipped_no_key');
  const anyUnavailable = record.items.some((i) => i.transcript_status === 'unavailable');

  // Header time reflects reality: measured talk time when transcribed, else the
  // total recorded duration (so the no-key path never reads a misleading 0:00).
  const recordedTotal = record.items.reduce((sum, it) => sum + (it.duration_s || 0), 0);
  const headerTime = s && s.total_talk_time_s > 0 ? s.total_talk_time_s : recordedTotal;

  const playingUrl = useRef<string | null>(null);
  function play(item: SessionItem) {
    if (!item.audio_blob) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // Revoke the previously-playing clip's URL before creating the next (L3).
    if (playingUrl.current) {
      URL.revokeObjectURL(playingUrl.current);
      playingUrl.current = null;
    }
    const url = URL.createObjectURL(item.audio_blob);
    playingUrl.current = url;
    const a = new Audio(url);
    audioRef.current = a;
    setPlaying(item.question_id + item.audio_mime);
    a.onended = () => {
      setPlaying(null);
      URL.revokeObjectURL(url);
      if (playingUrl.current === url) playingUrl.current = null;
    };
    a.play().catch(() => setPlaying(null));
  }

  async function doExport() {
    try {
      const blob = await exportSessionHTML(record);
      downloadBlob(blob, `ielts-session-${record.session_id}.html`);
      toast('Session exported — a self-contained HTML file with your audio.');
    } catch {
      toast('Export failed — see console.', 'err');
    }
  }

  return (
    <section className="screen-review">
      <div className="rv-head">
        <div className="rv-title">Session complete</div>
        <div className="rv-meta">
          Part {record.mode === 'part3_discussion' ? 3 : 1} · {record.items.length} question
          {record.items.length === 1 ? '' : 's'} · {fmtClock(headerTime)}
        </div>
      </div>

      {skippedNoKey && (
        <div className="notice">
          Transcripts and metrics were skipped — no transcription key was set. Your answers are still recorded and
          replayable below. Add an OpenAI key in Settings to see your pace and fillers next time.
        </div>
      )}
      {anyUnavailable && !skippedNoKey && (
        <div className="notice">
          Some answers couldn't be transcribed (a provider hiccup). Those recordings are kept and replayable.
        </div>
      )}

      <div className="summary">
        <div className="stat"><div className="n">{s?.avg_wpm ?? 0}<small>wpm</small></div><div className="l">Average pace</div></div>
        <div className="stat"><div className="n">{s?.total_fillers ?? 0}</div><div className="l">Filler words</div></div>
        <div className="stat"><div className="n">{s?.avg_response_latency_s ?? 0}<small>s</small></div><div className="l">Avg. time to start</div></div>
        <div className="stat"><div className="n">{s ? fmtClock(s.total_talk_time_s) : '0:00'}</div><div className="l">Total talk time</div></div>
      </div>

      {record.items.map((item, idx) => (
        <div className="qa" key={item.question_id + idx}>
          <div className="qa-top">
            <button
              className="play"
              onClick={() => play(item)}
              disabled={!item.audio_blob}
              aria-label={`Play your answer to: ${item.question_text}`}
            >
              <svg viewBox="0 0 10 12"><path d="M0 0l10 6-10 6z" /></svg>
            </button>
            <div className="qa-q">{item.question_text}</div>
          </div>
          <TranscriptLine item={item} isPlaying={playing === item.question_id + item.audio_mime} />
          {item.metrics ? (
            <div className="mchips">
              <span className="mchip"><b>{item.metrics.wpm}</b> wpm</span>
              <span className="mchip"><b>{item.metrics.filler_count}</b> filler{item.metrics.filler_count === 1 ? '' : 's'}</span>
              <span className="mchip"><b>{item.metrics.response_latency_s} s</b> to start</span>
              <span className="mchip"><b>{Math.round(item.metrics.talk_time_s)} s</b></span>
              {item.metrics.long_pauses > 0 && <span className="mchip"><b>{item.metrics.long_pauses}</b> long pause{item.metrics.long_pauses === 1 ? '' : 's'}</span>}
            </div>
          ) : null}
        </div>
      ))}

      <div className="rv-foot">
        <button className="btn-primary" onClick={onAgain}>Practice again</button>
        <button className="btn-line" onClick={doExport}>Export session</button>
      </div>
      <div className="vstr">v{APP_VERSION}</div>
    </section>
  );
}

function TranscriptLine({ item, isPlaying }: { item: SessionItem; isPlaying: boolean }) {
  if (item.transcript_status === 'no_speech') {
    return <p className="qa-t empty">No speech was detected in this answer.</p>;
  }
  if (item.transcript_status === 'skipped_no_key') {
    return <p className="qa-t empty">{isPlaying ? 'Playing…' : 'Recorded — transcript skipped (no key).'}</p>;
  }
  if (item.transcript_status === 'unavailable') {
    return <p className="qa-t empty">Transcript unavailable — the recording is kept.</p>;
  }
  if (!item.transcript) {
    return <p className="qa-t empty">…</p>;
  }
  return (
    <p className="qa-t">
      {segmentTranscript(item.transcript).map((seg, i) =>
        seg.filler ? (
          <span className="fill" key={i}>{seg.text}</span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </p>
  );
}
